import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/app/auth-context'

/**
 * Tiny guard wrapper for authenticated routes. If the auth context says
 * there's no live session (and we're not still booting), redirect to
 * `/login`. While auth is still warming up (`loading`), render nothing —
 * the page would flicker otherwise.
 *
 * The router also has a `beforeLoad` guard that reads sessionStorage
 * directly; this component is a belt-and-suspenders second check that
 * also handles in-app sign-out (idle timeout, manual logout).
 */
export interface AuthGateProps {
  children: ReactNode
}

export function AuthGate({ children }: AuthGateProps) {
  const { session, loading, endReason } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!loading && !session) {
      // Idle expiry surfaces the "session expired" banner on login.
      const search: Record<string, unknown> = endReason === 'idle' ? { expired: 1 } : {}
      void navigate({ to: '/login', search: search as Record<string, never> })
    }
  }, [loading, session, endReason, navigate])

  if (loading || !session) return null
  return <>{children}</>
}
